// OneDrive:IgnoreCodeCoverage

import SharedDateTimeResx from '@ms/utilities-date-time/lib/DateTime.resx';
import DateTimeResx from './DateTime.resx';
import Locale from '../locale/Locale';
import { format } from '../string/StringHelper';
import Features from '../features/Features';
import * as DateTime from '@ms/utilities-date-time/lib/index';

export * from '@ms/utilities-date-time/lib/index';

let _getLocale: () => string;
let supportsTimeZoneDateOptions: boolean;

let shortDateFormat: Intl.DateTimeFormat;
let shortTimeFormat: Intl.DateTimeFormat;
let shortDateFormatUTC: Intl.DateTimeFormat;
let shortTimeFormatUTC: Intl.DateTimeFormat;

let formatShortDate: (date: Date) => string;
let formatShortTime: (date: Date) => string;
let formatShortDateUTC: (date: Date) => string;
let formatShortTimeUTC: (date: Date) => string;

let _chromiumDateFixFeatureEnabled: boolean = undefined;
const ChromiumDateFix = { ODB: 1469, ODC: 'ChromiumDateFix' };

/**
 * Get a string like "X minutes ago" that reflects the time elapsed since the input time.
 * Only works for past times, future times just return a browser-determined localized time string.
 */
export function getRelativeDateTimeStringPast(pastTime: Date, startWithLowerCase: boolean = false): string {
    return DateTime.getRelativeDateTimeStringPast(pastTime, getBrowserSafeLocale(Locale.language), startWithLowerCase);
}

/**
 * Get a string that reflects the time elapsed since the input time.
 * Only works for past times, future times just return a browser-determined localized time string.
 */
export function getFriendlyDateTimeStringPast(pastTime: Date, shortDate: boolean = false, startWithLowerCase: boolean = false, useUTCTimezone: boolean = false): string {
    let timespan: number = Date.now() - pastTime.getTime(); // time elapsed in ms
    let todayMidnight = _getLocalMidnight();
    let yesterday = _addDays(todayMidnight, -1);
    let startOfWeek = _addDays(todayMidnight, -6);

    let language = getBrowserSafeLocale(Locale.language);
    if (timespan < -5 * DateTime.ONE_MINUTE) { // in the future, with a 5m leeway
        return pastTime.toLocaleDateString(language, _computeFormatOptionsWithTimeZoneParam({ month: 'short', day: 'numeric' }, useUTCTimezone));
    } else if (timespan < DateTime.TWO_MINUTES) {
        // "Just now"
        return startWithLowerCase ? DateTimeResx.FriendlyDateTime_JustNow_withinSentence : DateTimeResx.FriendlyDateTime_JustNow_asSentence;
    } else if (timespan < DateTime.ONE_HOUR) {
        // "{0}m ago"
        return _getTimespanString(timespan, DateTimeResx.FriendlyDateTime_minutesShort, DateTime.ONE_MINUTE);
    } else if (DateTime.isSameDay(todayMidnight, pastTime)) {
        // "{0}h ago"
        return _getTimespanString(timespan, DateTimeResx.FriendlyDateTime_hoursShort, DateTime.ONE_HOUR);
    } else if (pastTime >= yesterday) {
        if (shortDate) { // yesterday or Yesterday
            return DateTime.getRelativeDateTimeResx('Yesterday', startWithLowerCase);
        }
        // "Yesterday at {0}"
        return DateTime.getRelativeDateTimeResx('YesterdayAndTime', startWithLowerCase)
            .replace("{0}", pastTime.toLocaleTimeString(language, _computeFormatOptionsWithTimeZoneParam({ hour: 'numeric', minute: '2-digit' }, useUTCTimezone)));
    } else if (pastTime >= startOfWeek) {
        const shortWeekday = pastTime.toLocaleDateString(language, _computeFormatOptionsWithTimeZoneParam({ weekday: 'short' }, useUTCTimezone));
        if (shortDate) { // weekday
            return shortWeekday;
        }
        // {0} at {1}  weekday at time
        return SharedDateTimeResx.RelativeDateTime_DayAndTime.replace("{0}", shortWeekday).replace("{1}", pastTime.toLocaleTimeString(language, _computeFormatOptionsWithTimeZoneParam({ hour: 'numeric', minute: '2-digit' }, useUTCTimezone)));
    } else if (DateTime.isSameYear(todayMidnight, pastTime)) {
        // This is a workaround for the IE/Edge bug that omits the month character when using option with { month: 'short', day: 'numeric' }
        // TODO: watch the bug logged on IE/Edge and remove this workaround when that bug is fixed.
        if (language.match(/(^ja$)|(^ja-{1})/i)) {
            return pastTime.toLocaleDateString(language, _computeFormatOptionsWithTimeZoneParam({ month: 'long', day: 'numeric' }, useUTCTimezone));
        }
        return pastTime.toLocaleDateString(language, _computeFormatOptionsWithTimeZoneParam({ month: 'short', day: 'numeric' }, useUTCTimezone));
    }

    // Any other time, just return the regular full original time
    return pastTime.toLocaleDateString(language, _computeFormatOptionsWithTimeZoneParam({ month: 'short', day: 'numeric', year: 'numeric' }, useUTCTimezone)); // browser-determined localized date (no time)
}

/**
 * This is a modified implementation of `DateTime.GetRelativeDateTimeStringPast(...)`.
 * The differences here are as follows:
 *
 *   1. The time string for yesterday does not include the seconds
 *   2. Instead of showing 'X days ago' for dates older than a month, default to showing the full date
 *   3. The full date will also include the time (also without seconds)
 */
export function getRelativeDateTimeStringPastWithHourMinute(pastTime: Date) {
    return DateTime.getRelativeDateTimeStringPastWithHourMinute(pastTime, getBrowserSafeLocale(Locale.language));
}

function createShortDateFormatters(): void {
    let locale = _getLocale();
    let supportsUTC = _supportsTimeZoneDateOptions();
    if (window['Intl'] && window['Intl']['DateTimeFormat']) {
        let dateOptions: Intl.DateTimeFormatOptions = {};
        let timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

        shortDateFormat = new Intl.DateTimeFormat(locale, dateOptions);
        shortTimeFormat = new Intl.DateTimeFormat(locale, timeOptions);

        if (supportsUTC) {
            dateOptions.timeZone = 'UTC';
            timeOptions.timeZone = 'UTC';
        }

        shortDateFormatUTC = new Intl.DateTimeFormat(locale, dateOptions);
        shortTimeFormatUTC = new Intl.DateTimeFormat(locale, timeOptions);

        formatShortDate = shortDateFormat.format;
        formatShortTime = shortTimeFormat.format;
        formatShortDateUTC = shortDateFormatUTC.format;
        formatShortTimeUTC = shortTimeFormatUTC.format;
    } else {
        // No support for formatter objects.
        formatShortDate = formatShortDateUTC = (date: Date) => {
            return date.toLocaleDateString(locale);
        };
        formatShortTime = formatShortTimeUTC = (date: Date) => {
            return date.toLocaleTimeString(locale);
        };
    }
}

/**
 * Returns a short version of a date to display (e.g. 11:45 PM if today, or 11/2/2015 if not today)
 */
export function getShortDisplayDate(date: Date, useUTCTimezone?: boolean): string {
    if (!date) {
        return '';
    }

    if (!formatShortDate) {
        createShortDateFormatters();
    }

    const isToday = DateTime.isSameDay(new Date(), date);

    let formatter: (date: Date) => string;
    if (useUTCTimezone) {
        if (isToday) {
            formatter = formatShortTimeUTC;
        } else {
            formatter = formatShortDateUTC;
        }
    } else {
        if (isToday) {
            formatter = formatShortTime;
        } else {
            formatter = formatShortDate;
        }
    }

    return formatter(date);
}

/**
 * Returns a short version of a date valid for use in a file name
 */
export function getFileSafeShortDate(date: Date, useUTCTimezone?: boolean): string {
    if (!date) {
        return '';
    }

    if (!formatShortDate) {
        createShortDateFormatters();
    }

    const formatter: (date: Date) => string = useUTCTimezone ? formatShortDateUTC : formatShortDate;

    const formatted = formatter(date);
    // Replace characters that are not path-safe
    const safe = formatted.replace(/[:\/\\]/g, '-');
    return safe;
}

/**
 * Returns a full version of a date to display (e.g. 11/2/2015 11:45 PM)
 */
export function getFullDisplayDate(date: Date, useUTCTimezone?: boolean): string {
    if (!formatShortDate) {
        createShortDateFormatters();
    }
    let dateString = useUTCTimezone ? formatShortDateUTC(date) : formatShortDate(date);
    let timeString = useUTCTimezone ? formatShortTimeUTC(date) : formatShortTime(date);
    return format(DateTimeResx.DateAndTime, dateString, timeString);
}

/**
 * Gets a string of the given date in the current site's locale, falling back to the browser's
 * current/default locale if needed.
 */
export function toLocaleDateString(date: Date, options?: Intl.DateTimeFormatOptions): string | undefined {
    return DateTime.toLocaleDateString(date, getBrowserSafeLocale(Locale.language), options);
}

/**
 * Gets a string of the given time in the current site's locale, falling back to the browser's
 * current/default locale if needed.
 */
export function toLocaleTimeString(date: Date, options?: Intl.DateTimeFormatOptions): string | undefined {
    return DateTime.toLocaleTimeString(date, getBrowserSafeLocale(Locale.language), options);
}

/**
 * Checks against locales that are known to be unsupported, and returns "en" if that is the case.
 * Returns input locale unchanged otherwise.
 */
export function getBrowserSafeLocale(locale: string) {
    if (_chromiumDateFixFeatureEnabled === undefined) {
        try {
            _chromiumDateFixFeatureEnabled = Features.isFeatureEnabled(ChromiumDateFix);
        } catch {
            _chromiumDateFixFeatureEnabled = false;
        }
    }

    if (_chromiumDateFixFeatureEnabled && _isBrowserUnsupportedLocale(locale)) {
        return "en";
    }

    return locale;
}

/**
 * Checks if date can be formatted properly for this locale in in the current browser runtime.
 * Currently only checks against known problematic locales in Chromium.
 */
function _isBrowserUnsupportedLocale(locale: string): boolean {
    if (_chromiumUnsupportedLocalesMap === undefined) {
        _initializeChromiumUnsupportedLocales();
    }

    if (locale) {
        // This check can be removed if the following bug is fixed:
        // https://bugs.chromium.org/p/chromium/issues/detail?id=935054
        locale = locale.toLowerCase();
        return _chromiumUnsupportedLocalesMap[locale];
    }

    return undefined;
}

_getLocale = () => {
    let validLocale: string;
    let locales: string[] = [
        window['$Config'] && window['$Config']['mkt'],
        Locale.language,
        navigator.language,
        'en'].filter((str: string) => !!str);

    for (let locale of locales) {
        try {
            new Date().toLocaleDateString(locale);
            validLocale = locale;
            break;
        } catch (e) {
            // Fall back to next candidate locale. Eventually the locale will be undefined, if no valid locale exists
        }
    }

    validLocale = getBrowserSafeLocale(validLocale);

    _getLocale = () => validLocale;

    return validLocale;
};

function _supportsTimeZoneDateOptions(): boolean {
    if (supportsTimeZoneDateOptions === void 0) {
        try {
            let locale = _getLocale();
            (new Date()).toLocaleDateString(locale, { timeZone: 'UTC' });
            supportsTimeZoneDateOptions = true;
        } catch (E) {
            // We know of some versions of IE 11 that fail when date options with a timezone is specified.
            supportsTimeZoneDateOptions = false;
        }
    }

    return supportsTimeZoneDateOptions;
}

/**
 * These are the language tags from our "supported list" that are officially unsupported by Chromium.
 */
var _chromiumUnsupportedLocales = ['eu-es', 'af-za', 'az-latn-az', 'bs-latn-ba', 'cy-gb', 'ga-ie', 'gl-es',
    'ha-latn-ng', 'hy-am', 'ig-ng', 'is-is', 'ka-ge', 'kk-kz', 'km-kh', 'ky-kg', 'mk-mk', 'mn-mn', 'mt-mt', 'ne-np',
    'nn-no', 'or-in', 'pa-in', 'si-lk', 'sq-al', 'ur-pk', 'uz-latn-uz', 'yo-ng', 'zu-za', 'pa-arab-pk', 'ku-arab-iq',
    'be-by', 'rw-rw', 'ti-et', 'tg-cyrl-tj', 'eu', 'af', 'az', 'bs', 'cy', 'ga', 'gl', 'ha', 'hy', 'ig', 'is', 'ka',
    'kk', 'km', 'ky', 'mk', 'mn', 'mt', 'ne', 'nn', 'or', 'pa', 'si', 'sq', 'ur', 'uz', 'yo', 'zu', 'pa', 'ku', 'be',
    'rw', 'ti'];

var _chromiumUnsupportedLocalesMap = undefined;

function _initializeChromiumUnsupportedLocales() {
    _chromiumUnsupportedLocalesMap = {};

    // See if the suspected locales are problematic in the current runtime.
    const date = new Date(1900, 0, 1);
    try {
        // Heuristic to recognize unsupported locales: Chromium returns "M01", "M02", etc as month (short/long)
        const options = { month: 'long' };
        for (const locale of _chromiumUnsupportedLocales) {
            const formatted = date.toLocaleDateString(locale, options);
            if (formatted.match("M[0-9][0-9]")) {
                _chromiumUnsupportedLocalesMap[locale] = true;
            }
        }
    } catch {
        // Ignore errors
    }
}

function _getLocalMidnight(): Date {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    return midnight;
}

function _addDays(day: Date, days: number): Date {
    return new Date(day.getFullYear(), day.getMonth(), day.getDate() + days, day.getHours(), day.getMinutes(), day.getSeconds(), day.getMilliseconds());
}

function _getTimespanString(timespan: number, locString: string, periodInSeconds: number): string {
    const displayNumber = Math.round(timespan / periodInSeconds);
    return locString.replace('{0}', String(displayNumber));
}

function _computeFormatOptionsWithTimeZoneParam(options: Intl.DateTimeFormatOptions, useUTCTimezone: boolean): Intl.DateTimeFormatOptions {
    if (_supportsTimeZoneDateOptions() && useUTCTimezone && options) {
        return {
            ...options,
            timeZone: 'UTC'
        };
    }
    return options;
}
